import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import MyStreamWaveDesktopTabs from "../MyStreamWaveDesktopTabs";
import { useContentTab } from "@/components/brain/ContentTabContext";
import { useSidebarState } from "../../../../hooks/useSidebarState";
import { ChevronDoubleLeftIcon, ArrowLeftIcon, MagnifyingGlassIcon, ChatBubbleLeftIcon, Squares2X2Icon, ShareIcon, LinkIcon, CheckIcon } from "@heroicons/react/24/outline";
import WavePicture from "../../../waves/WavePicture";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { createBreakpoint } from "react-use";
import WaveDropsSearchModal from "@/components/waves/drops/search/WaveDropsSearchModal";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveShareCopyAction } from "@/hooks/waves/useWaveShareCopyAction";
const useBreakpoint = createBreakpoint({
  LG: 1024,
  S: 0
});
interface MyStreamWaveTabsDefaultProps {
  readonly wave: ApiWave;
  readonly viewMode: WaveViewMode;
  readonly onToggleViewMode: () => void;
  readonly showGalleryToggle: boolean;
}
const MyStreamWaveTabsDefault: React.FC<MyStreamWaveTabsDefaultProps> = t0 => {
  const $ = _c(71);
  const {
    wave,
    viewMode,
    onToggleViewMode,
    showGalleryToggle
  } = t0;
  const {
    activeContentTab,
    setActiveContentTab
  } = useContentTab();
  const {
    toggleRightSidebar,
    isRightSidebarOpen
  } = useSidebarState();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "S";
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const waveChatScroll = useWaveChatScrollOptional();
  const isDirectMessage = wave.chat.scope.group?.is_direct_message ?? false;
  const showShareAction = !isDirectMessage;
  let t1;
  if ($[0] !== isDirectMessage || $[1] !== wave.id || $[2] !== wave.name) {
    t1 = {
      waveId: wave.id,
      waveName: wave.name,
      isDirectMessage
    };
    $[0] = isDirectMessage;
    $[1] = wave.id;
    $[2] = wave.name;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    mode: waveLinkActionMode,
    label: waveLinkActionLabel,
    feedbackState: waveLinkActionFeedbackState,
    onClick: handleWaveLinkActionClick
  } = useWaveShareCopyAction(t1);
  let t2;
  if ($[4] !== isApp || $[5] !== router || $[6] !== searchParams || $[7] !== wave.chat.scope.group?.is_direct_message) {
    t2 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      params.delete("serialNo");
      params.delete("divider");
      params.delete("drop");
      const basePath = getWaveHomeRoute({
        isDirectMessage: wave.chat.scope.group?.is_direct_message ?? false,
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[4] = isApp;
    $[5] = router;
    $[6] = searchParams;
    $[7] = wave.chat.scope.group?.is_direct_message;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleMobileBack = t2;
  let t3;
  if ($[9] !== pathname || $[10] !== router || $[11] !== searchParams || $[12] !== setActiveContentTab || $[13] !== wave.id || $[14] !== waveChatScroll) {
    t3 = serialNo => {
      setActiveContentTab(MyStreamWaveTab.CHAT);
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("serialNo", String(serialNo));
      router.replace(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[9] = pathname;
    $[10] = router;
    $[11] = searchParams;
    $[12] = setActiveContentTab;
    $[13] = wave.id;
    $[14] = waveChatScroll;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const handleSearchSelect = t3;
  const waveLinkActionIconColor = waveLinkActionFeedbackState === "idle" ? "tw-text-iron-200" : "tw-text-emerald-300";
  let t4;
  if ($[16] !== waveLinkActionFeedbackState || $[17] !== waveLinkActionMode) {
    t4 = () => {
      if (waveLinkActionFeedbackState !== "idle") {
        return <CheckIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      if (waveLinkActionMode === "share") {
        return <ShareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      return <LinkIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    };
    $[16] = waveLinkActionFeedbackState;
    $[17] = waveLinkActionMode;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const renderWaveLinkActionIcon = t4;
  let t5;
  if ($[19] !== handleMobileBack || $[20] !== isMobile) {
    t5 = isMobile && <button onClick={handleMobileBack} className="-tw-ml-2.5 tw-mr-1.5 tw-flex tw-h-full tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-px-2.5 tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50" aria-label="Go back"><ArrowLeftIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></button>;
    $[19] = handleMobileBack;
    $[20] = isMobile;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== handleWaveLinkActionClick || $[23] !== isMobile || $[24] !== onToggleViewMode || $[25] !== renderWaveLinkActionIcon || $[26] !== showGalleryToggle || $[27] !== showShareAction || $[28] !== viewMode || $[29] !== wave.contributors_overview || $[30] !== wave.name || $[31] !== wave.picture || $[32] !== waveLinkActionIconColor || $[33] !== waveLinkActionLabel || $[34] !== waveLinkActionMode) {
    t6 = isMobile ? <button type="button" onClick={() => setIsSearchOpen(true)} aria-label="Search messages in this wave" className="tw-flex tw-min-w-0 tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"><div className="tw-size-6 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950 lg:tw-size-9"><WavePicture name={wave.name} picture={wave.picture} contributors={wave.contributors_overview.map(_temp)} /></div><h1 className="tw-mb-0 tw-ml-3 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1></button> : <><div className="tw-size-6 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950 lg:tw-size-9"><WavePicture name={wave.name} picture={wave.picture} contributors={wave.contributors_overview.map(_temp2)} /></div><h1 className="tw-mb-0 tw-ml-3 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1>{showGalleryToggle && <button type="button" onClick={onToggleViewMode} aria-label={viewMode === "chat" ? "Switch to gallery view" : "Switch to chat view"} className="tw-ml-2 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{viewMode === "chat" ? <Squares2X2Icon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /> : <ChatBubbleLeftIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />}</button>}{showShareAction && <button type="button" onClick={handleWaveLinkActionClick} aria-label={waveLinkActionLabel} title={waveLinkActionLabel} data-wave-link-action-mode={waveLinkActionMode} className={`tw-ml-2 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white ${waveLinkActionIconColor}`}>{renderWaveLinkActionIcon()}</button>}<button type="button" onClick={() => setIsSearchOpen(true)} aria-label="Search messages in this wave" className="tw-ml-2 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white"><MagnifyingGlassIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /></button></>;
    $[22] = handleWaveLinkActionClick;
    $[23] = isMobile;
    $[24] = onToggleViewMode;
    $[25] = renderWaveLinkActionIcon;
    $[26] = showGalleryToggle;
    $[27] = showShareAction;
    $[28] = viewMode;
    $[29] = wave.contributors_overview;
    $[30] = wave.name;
    $[31] = wave.picture;
    $[32] = waveLinkActionIconColor;
    $[33] = waveLinkActionLabel;
    $[34] = waveLinkActionMode;
    $[35] = t6;
  } else {
    t6 = $[35];
  }
  let t7;
  if ($[36] !== t5 || $[37] !== t6) {
    t7 = <div className="tw-flex tw-min-w-0 tw-items-center">{t5}{t6}</div>;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== handleWaveLinkActionClick || $[40] !== isMobile || $[41] !== renderWaveLinkActionIcon || $[42] !== showShareAction || $[43] !== waveLinkActionIconColor || $[44] !== waveLinkActionLabel || $[45] !== waveLinkActionMode) {
    t8 = isMobile && showShareAction && <button type="button" onClick={handleWaveLinkActionClick} aria-label={waveLinkActionLabel} title={waveLinkActionLabel} data-wave-link-action-mode={waveLinkActionMode} className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white ${waveLinkActionIconColor}`}>{renderWaveLinkActionIcon()}</button>;
    $[39] = handleWaveLinkActionClick;
    $[40] = isMobile;
    $[41] = renderWaveLinkActionIcon;
    $[42] = showShareAction;
    $[43] = waveLinkActionIconColor;
    $[44] = waveLinkActionLabel;
    $[45] = waveLinkActionMode;
    $[46] = t8;
  } else {
    t8 = $[46];
  }
  const t9 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition tw-duration-300 ${isRightSidebarOpen ? "tw-rotate-180 desktop-hover:group-hover:tw-translate-x-0.5" : "tw-rotate-0 desktop-hover:group-hover:-tw-translate-x-0.5"}`;
  let t10;
  if ($[47] !== t9) {
    t10 = <ChevronDoubleLeftIcon strokeWidth={2} className={t9} />;
    $[47] = t9;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== t10 || $[50] !== toggleRightSidebar) {
    t11 = <button type="button" onClick={toggleRightSidebar} className="tw-group tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/80 desktop-hover:hover:tw-bg-iron-700/85 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]" aria-label="Toggle right sidebar">{t10}</button>;
    $[49] = t10;
    $[50] = toggleRightSidebar;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] !== t11 || $[53] !== t8) {
    t12 = <div className="tw-flex tw-items-center tw-gap-x-2 tw-self-stretch">{t8}{t11}</div>;
    $[52] = t11;
    $[53] = t8;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  let t13;
  if ($[55] !== t12 || $[56] !== t7) {
    t13 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-px-2 tw-py-3 sm:tw-px-4">{t7}{t12}</div>;
    $[55] = t12;
    $[56] = t7;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== activeContentTab || $[59] !== setActiveContentTab || $[60] !== wave) {
    t14 = <div className="tw-border-b tw-border-l-0 tw-border-t-0 tw-border-solid tw-border-iron-800"><MyStreamWaveDesktopTabs activeTab={activeContentTab} wave={wave} setActiveTab={setActiveContentTab} /></div>;
    $[58] = activeContentTab;
    $[59] = setActiveContentTab;
    $[60] = wave;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  let t15;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsSearchOpen(false);
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  let t16;
  if ($[63] !== handleSearchSelect || $[64] !== isSearchOpen || $[65] !== wave) {
    t16 = <WaveDropsSearchModal isOpen={isSearchOpen} onClose={t15} wave={wave} onSelectSerialNo={handleSearchSelect} />;
    $[63] = handleSearchSelect;
    $[64] = isSearchOpen;
    $[65] = wave;
    $[66] = t16;
  } else {
    t16 = $[66];
  }
  let t17;
  if ($[67] !== t13 || $[68] !== t14 || $[69] !== t16) {
    t17 = <div className="tw-flex tw-w-full tw-flex-col tw-bg-iron-950">{t13}{t14}{t16}</div>;
    $[67] = t13;
    $[68] = t14;
    $[69] = t16;
    $[70] = t17;
  } else {
    t17 = $[70];
  }
  return t17;
};
export default MyStreamWaveTabsDefault;
function _temp(c) {
  return {
    pfp: c.contributor_pfp
  };
}
function _temp2(c_0) {
  return {
    pfp: c_0.contributor_pfp
  };
}